import { createFileRoute, redirect } from '@tanstack/react-router'
import { type } from 'arktype'
import { type ReactElement } from 'react'

import { computeRecoveryTotal } from '#/components/pages/unpaid-invoices-recovery/compute-recovery-total.ts'
import { UnpaidInvoicesPaymentPageSkeleton } from '#/modules/invoice/front-office/unpaid-invoices-payment-page/unpaid-invoices-payment-page-skeleton.tsx'
import { UnpaidInvoicesPaymentPage } from '#/modules/invoice/front-office/unpaid-invoices-payment-page/unpaid-invoices-payment-page.tsx'
import { frontOfficeQueryOptions } from '#/shared/front-office-query-options.ts'
import { requirePermissions } from '#/shared/routing/require-permissions.ts'

const searchSchema = type({ invoices: 'string[]' })

export const Route = createFileRoute('/(customer)/_shell/unpaid-invoices/payment')({
  validateSearch: searchSchema,
  beforeLoad: (options) => requirePermissions(options, ['invoice.pay']),
  loaderDeps: ({ search }) => ({ invoices: search.invoices }),
  loader: async ({ context, deps }) => {
    const { queryClient, trpc } = context

    // No prefetch of the Stripe publishable key here, deliberately: `useStripeElementsProps` reads it
    // through `queryClient.fetchQuery`, and with the global client's `staleTime: 0` that always refetches
    // while `gcTime: 0` evicts an observer-less prefetch as soon as it settles. Warming it would cost a
    // request and save nothing — the key is fetched at mount and the card area is blank until it lands.
    const groups = await queryClient.ensureQueryData(
      trpc.invoices.getMyRecoverableInvoices.queryOptions(undefined, frontOfficeQueryOptions),
    )

    // Nothing left to pay for the requested ids — a stale link, or the invoices were settled meanwhile.
    // The legacy screen handled this with an effect that raced the post-payment navigation and could
    // strand the user on a spinner; deciding before the screen mounts removes both problems.
    if (computeRecoveryTotal(groups, deps.invoices).matchedInvoiceIds.length === 0) {
      throw redirect({ to: '/home', replace: true })
    }
  },
  pendingComponent: UnpaidInvoicesPaymentPageSkeleton,
  pendingMs: 150,
  component: UnpaidInvoicesPaymentRoute,
})

function UnpaidInvoicesPaymentRoute(): ReactElement {
  const { invoices } = Route.useSearch()
  return <UnpaidInvoicesPaymentPage invoices={invoices} />
}
